import { ListChecks, Plus } from 'lucide-react'
import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useNavigate } from 'react-router'

import { AskCai } from '@/components/AskCai/AskCai'
import { OnboardingBannerSlot } from '@/components/OnboardingBannerSlot'
import { useCaiDocked } from '@/components/sidebar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { GeneratedBlankState } from '@/components/ui/generated-blank-state'
import { ResponsiveTable } from '@/components/ui/responsive-table'
import { SkeletonRows } from '@/components/ui/skeleton'
import { UnderlineTabs } from '@/components/ui/UnderlineTabs'
import { formatCurrency, formatDate } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import { type PolicyChangeRow, usePolicyChanges } from '@/models/policyChange'
import { hasPolicyInForce, useCurrentUser, worksFromQuotesPage } from '@/models/user'
import { changeCountsLabel, CLOSED_STATUSES } from './policyChangeCopy'
import { PolicyChangesGate } from './PolicyChangesGate'
import { PolicyChangeStatusBadge } from './PolicyChangeStatusBadge'

enum Tab {
  OPEN = 'open',
  HISTORY = 'history',
}

const COLUMNS = [
  {
    key: 'request',
    header: 'Request',
    hideOnCard: true,
    cell: (row: PolicyChangeRow) => <span className="font-mono text-sm">{row.idShort}</span>,
  },
  { key: 'changes', header: 'Changes', cell: (row: PolicyChangeRow) => changeCountsLabel(row.changeCounts) },
  {
    key: 'effective',
    header: 'Effective from',
    cell: (row: PolicyChangeRow) => formatDate(row.earliestEffectiveDate),
  },
  {
    key: 'price',
    header: 'Price change',
    cell: (row: PolicyChangeRow) =>
      row.proratedChangeTotal === null ? '-' : formatCurrency(Number(row.proratedChangeTotal)),
  },
  {
    key: 'status',
    header: 'Status',
    cell: (row: PolicyChangeRow) => (
      <PolicyChangeStatusBadge
        status={row.status}
        submitted={!!row.submittedAt}
        waitingOnCustomer={row.waitingOnCustomer}
      />
    ),
  },
]

export function PolicyChanges() {
  const navigate = useNavigate()
  const caiDocked = useCaiDocked()
  const { user } = useCurrentUser({ enabled: true })
  const { requests, isLoading, isError } = usePolicyChanges(user?.currentOrg.id)
  const [tab, setTab] = useState(Tab.OPEN)
  const open = requests.filter((request) => !CLOSED_STATUSES.includes(request.status))
  const history = requests.filter((request) => CLOSED_STATUSES.includes(request.status))
  const hasRequests = requests.length > 0
  // Shown before coverage so a fleet sees what's coming; there's no policy to change yet.
  const covered = hasPolicyInForce(user?.currentOrg)
  const requestButton = covered ? (
    <Button asChild>
      <Link to="/insure/policy-changes/new">
        <Plus />
        Request a change
      </Link>
    </Button>
  ) : (
    <Button disabled>
      <Plus />
      Request a change
    </Button>
  )

  return (
    <PolicyChangesGate>
      <Helmet>
        <title>Policy changes</title>
      </Helmet>

      <h1 className="text-ink mb-4 text-2xl font-bold tracking-[-0.01em]">Policy changes</h1>
      <OnboardingBannerSlot className="mb-4" />
      <div className={cn('grid grid-cols-1 gap-6', hasRequests && 'lg:grid-cols-[3fr_1fr]')}>
        <div className="flex flex-col gap-4">
          {!isLoading && !isError && !hasRequests && (
            <GeneratedBlankState icon={ListChecks} action={requestButton}>
              {!covered &&
                'Once your coverage starts, you can ask for changes here, like adding a truck or a driver, and get an updated COI.'}
            </GeneratedBlankState>
          )}
          {hasRequests && (
            <UnderlineTabs
              tabs={[
                { value: Tab.OPEN, label: `Open (${open.length})` },
                { value: Tab.HISTORY, label: `History (${history.length})` },
              ]}
              value={tab}
              onValueChange={(value) => setTab(value as Tab)}
            />
          )}
          {isLoading && <SkeletonRows rows={4} />}
          {isError && (
            <p className="text-destructive text-sm">
              We couldn&rsquo;t load your policy changes. Refresh to try again.
            </p>
          )}
          {!isLoading && !isError && hasRequests && (
            <ResponsiveTable
              items={tab === Tab.OPEN ? open : history}
              columns={COLUMNS}
              getRowId={(row) => row.id}
              cardTitle={(row) => `Request ${row.idShort}`}
              onRowClick={(row) => navigate(`/insure/policy-changes/${row.id}`)}
              empty={tab === Tab.OPEN ? 'No open policy changes.' : 'No finished policy changes yet.'}
            />
          )}
        </div>

        <div className={cn('flex flex-col gap-4', !hasRequests && 'max-w-2xl')}>
          {!caiDocked && !worksFromQuotesPage(user?.currentOrg) && <AskCai />}
          {(hasRequests || isError) && requestButton}
          {covered && (
            <Card>
              <CardHeader>
                <CardTitle>How it works</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="text-ink-600 list-decimal space-y-1 pl-5 text-sm">
                  <li>You tell us what's changing.</li>
                  <li>Your insurance broker reviews it and sends it to your insurance providers.</li>
                  <li>You accept or decline their quote. Nothing changes until you accept.</li>
                  <li>Your policies update and you get a new COI.</li>
                </ol>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </PolicyChangesGate>
  )
}
